import { css } from '@emotion/react';
import prodPathReplace from 'shared/prod-path-rewrite';
import {
  black,
  gray1,
  gray2,
  gray4,
  hexToRgbaString,
  info,
  kelp,
  primary,
  success,
  warning,
  white,
} from 'styles/global_defaults/colors';
import {
  doubleSpacing,
  extraLargeSpacing,
  halfSpacing,
  largeSpacing,
  quarterSpacing,
  standardSpacing,
  threeQuartersSpacing,
  tripleSpacing,
} from 'styles/global_defaults/scaffolding';
import {
  screenSmMax,
  screenXsMax,
} from 'styles/global_defaults/media-queries';

const successGradient = `linear-gradient(145deg, ${success} 0%, ${kelp} 100%)`;
const compactLayoutMaxWidth = screenXsMax + 1;
const oneColumnStatsMaxWidth = 480;

const fullPageStyles = css`
  position: relative;
  min-height: 100vh;
  background: ${info};
  overflow: hidden;
  padding: ${doubleSpacing}px ${standardSpacing}px ${extraLargeSpacing}px;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 540px;
    background-image: url(${prodPathReplace('images/personalized-takeaways-top-wave.svg')});
    background-repeat: no-repeat;
    background-size: 100% 100%;
    background-position: top left;
    z-index: 0;
  }

  .takeaways-content {
    position: relative;
    z-index: 1;
    max-width: 820px;
    margin: 0 auto;
    text-align: center;
  }

  .mobile-burger-bar {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: 56px;
    display: none;
    align-items: center;
    padding: 0 ${halfSpacing}px;
    background: transparent;
    transition: background-color 120ms ease, box-shadow 120ms ease;
    z-index: 3;

    &.scrolled {
      background: ${white};
      box-shadow: 0 1px 0 0 ${gray4};
    }
  }

  .mobile-burger-button {
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 6px;
    background: transparent;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
  }

  .takeaways-spark {
    display: inline-flex;
    margin-bottom: ${quarterSpacing}px;
  }

  .eyebrow {
    margin-bottom: ${halfSpacing}px;
  }

  .main-title {
    margin-bottom: ${tripleSpacing}px;
  }

  .divider {
    width: 100px;
    height: 1px;
    background: ${gray4};
    margin: 0 auto 44px;
  }

  .section {
    margin-bottom: 72px;

    .section-kicker {
      margin-bottom: ${halfSpacing}px;
    }

    .section-title {
      color: ${gray1};
      margin-bottom: ${standardSpacing}px;
    }

    .section-copy {
      max-width: 650px;
      margin: 0 auto;

      .section-headline,
      .section-summary,
      .section-intro,
      .section-empty {
        color: ${gray1};
      }

      .section-headline {
        margin-bottom: ${halfSpacing}px;
      }

      .section-intro {
        margin-top: ${halfSpacing}px;
      }

      .section-bullets {
        text-align: left;
        margin: ${standardSpacing}px auto 0;
        padding-left: 0;
        max-width: 650px;
        list-style: none;

        li {
          display: flex;
          align-items: flex-start;
          gap: ${halfSpacing}px;
        }

        li + li {
          margin-top: ${halfSpacing}px;
        }
      }

      .section-bullet-icon {
        color: ${warning};
        margin-top: ${quarterSpacing - 3}px;
        flex-shrink: 0;
      }

      .section-bullet-text {
        flex: 1;
      }

      .section-resources {
        margin-top: ${standardSpacing}px;
        display: grid;
        gap: ${threeQuartersSpacing - 1}px;

        .resource-card {
          text-align: left;
          display: flex;
          align-items: flex-start;
          gap: ${halfSpacing}px;

          .resource-icon {
            color: ${warning};
            margin-top: ${quarterSpacing - 3}px;
            flex-shrink: 0;
          }

          .resource-title {
            color: ${primary};
            text-decoration: underline;
          }

          .resource-reason {
            color: ${gray1};
            display: inline;
          }
        }
      }
    }
  }

  .generating-block {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${quarterSpacing}px;
    min-height: 70px;
  }

  .generating-label {
    color: ${gray2};
  }

  .stats-row {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: ${halfSpacing}px;
    margin-top: ${standardSpacing}px;
  }

  .stats-card {
    min-height: 92px;
    border-radius: ${halfSpacing}px;
    border: 1px solid ${hexToRgbaString(white, 0.9)};
    box-shadow: 0 2px 8px ${hexToRgbaString(black, 0.12)};
    background: ${hexToRgbaString(info, 0.9)};
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: ${halfSpacing}px;
    gap: ${quarterSpacing}px;
  }

  .mini-spinner {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    border: 2px solid ${hexToRgbaString(gray2, 0.2)};
    border-top-color: ${hexToRgbaString(gray2, 0.8)};
    animation: spin 0.9s linear infinite;
  }

  .completion-badge {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: ${successGradient};
    margin: 0 auto ${standardSpacing}px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 2px solid ${hexToRgbaString(white, 0.8)};

    .congrats-image {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  .completion-copy {
    max-width: 620px;
    margin: 0 auto;

    .section-title {
      margin-bottom: ${standardSpacing}px;
    }

    .completion-body {
      color: ${gray1};
      margin-bottom: ${largeSpacing}px;
    }
  }

  .back-button {
    min-height: 35px;
    border: none;
    border-radius: ${quarterSpacing}px;
    background: ${primary};
    color: ${white};
    padding: ${quarterSpacing}px ${threeQuartersSpacing}px;
  }

  .note {
    margin-top: ${largeSpacing}px;
    color: ${gray2};
  }

  @media (max-width: ${screenSmMax}px) {
    .stats-row {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }

  @media (max-width: ${compactLayoutMaxWidth}px) {
    padding: ${doubleSpacing}px ${halfSpacing}px ${doubleSpacing}px;

    &:before {
      background-position: calc(100% + 90px) top;
      background-size: 140% 100%;
    }

    .main-title {
      margin-bottom: ${doubleSpacing}px;
    }

    .stats-row {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: ${screenSmMax}px) {
    .mobile-burger-bar {
      display: flex;
    }
  }

  @media (max-width: ${oneColumnStatsMaxWidth}px) {
    .stats-row {
      grid-template-columns: 1fr;
    }
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export default fullPageStyles;
